import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { AlertWarningTriangleIcon, ArrowExpandIcon } from '@calendly/ui/components/icons';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { useGetUserQuery } from 'common';
const containerIsDisabledCss = "c1n51hju";
const containerColumnCss = "cug9bnm";
const containerNewColorCss = "c1490ze5";
const containerHasBorderCss = "c1geo72k";
const containerCss = "c12cegh6";
const Container = forwardRef((props, ref) => {
  const {
    isDisabled,
    column,
    newColor,
    hasBorder,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(containerCss, {
    [containerIsDisabledCss]: isDisabled,
    [containerColumnCss]: column,
    [containerNewColorCss]: newColor,
    [containerHasBorderCss]: hasBorder
  }, props.className)} />;
});
const messageContainerCss = "m141pdfz";
const MessageContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(messageContainerCss, props.className)} />;
});
const iconWrapperCss = "i1eb6by9";
const IconWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(iconWrapperCss, props.className)} />;
});
const buttonWrapperShiftRightCss = "b10rf9cd";
const buttonWrapperCss = "bn61vai";
const ButtonWrapper = forwardRef((props, ref) => {
  const {
    shiftRight,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(buttonWrapperCss, {
    [buttonWrapperShiftRightCss]: shiftRight
  }, props.className)} />;
});
const styledTextCss = "s1dxeq1y";
const StyledText = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(styledTextCss, props.className)} />;
});
export const CONFERENCE_ERROR_CONFIG = {
  zoom_conference: {
    conferenceName: 'Zoom',
    link: '/integrations/zoom'
  },
  gotomeeting_conference: {
    conferenceName: 'GoTo Meeting',
    link: '/integrations/gotomeeting'
  },
  microsoft_teams_conference: {
    conferenceName: 'Microsoft Teams',
    link: '/integrations/microsoft_teams'
  },
  webex_conference: {
    conferenceName: 'Webex',
    link: '/integrations/webex'
  },
  google_conference: {
    conferenceName: 'Google',
    link: '/app/availability/connected_calendars'
  }
};

/**
 *
 * @param {{
 *  kind: string,
 *  config: Record<string, {conferenceName: string, link: string}>,
 *  forCurrentUser: boolean,
 *  organizerName: string
 * }} props
 * @returns
 */
// i18n-tasks-use t('publisher.event_type_editor.sections.event_details.location.conferencing_not_connected.other')
// i18n-tasks-use t('publisher.event_type_editor.sections.event_details.location.conferencing_not_connected.you')
const newConferenceErrorMessage = ({
  kind,
  organizer,
  forCurrentUser
}) => {
  const {
    conferenceName
  } = CONFERENCE_ERROR_CONFIG[kind];
  if (organizer?.isPending || organizer?.is_pending) {
    return I18n.t('publisher.event_types.location.conferencing_pending_user', {
      conferencing_name: conferenceName
    });
  }
  if (!forCurrentUser) {
    return I18n.t('publisher.event_type_editor.sections.event_details.location.specific_conferencing_not_connected.other', {
      kindName: conferenceName,
      name: organizer?.name
    });
  }
  if (kind === 'google_conference') {
    return I18n.t('publisher.event_type_editor.sections.event_details.location.google_conference.calendar_connection_warning');
  }
  return I18n.t('publisher.event_type_editor.sections.event_details.location.specific_conferencing_not_connected.you', {
    kindName: conferenceName
  });
};

/**
 * @param {{
 *  config: {conferenceName: string, link: string},
 *  withExternalIcon: boolean
 * }} props
 */
const ConnectButton = ({
  config: {
    conferenceName,
    link
  },
  withExternalIcon = false
}) => <Button as="a" href={link} target="_blank" rel="noopener noreferrer" size="small" decoration="secondary-outline">
    {I18n.t('publisher.event_type_editor.sections.event_details.location.conferencing_connect_button', {
    conferencing_name: conferenceName
  })}
    {withExternalIcon && <ArrowExpandIcon />}
  </Button>;

/**
 *
 * @param {{
 *  kind: string,
 *  organizer: {name: string},
 *  hasPendingUser: boolean,
 *  forCurrentUser: boolean
 * }} params
 */
export const NewConferenceError = ({
  kind,
  organizer,
  hasBorder = false,
  ...props
}) => {
  const {
    data: currentUser
  } = useGetUserQuery();
  const memberUserUuid = organizer?.userUuid || organizer?.user_uuid;
  const forCurrentUser = memberUserUuid === currentUser.uuid;
  return <Container column newColor hasBorder={hasBorder} {...props}>
      <MessageContainer>
        <IconWrapper>
          <AlertWarningTriangleIcon />
        </IconWrapper>
        <StyledText>
          {newConferenceErrorMessage({
          kind,
          organizer,
          forCurrentUser
        })}
        </StyledText>
      </MessageContainer>
      {forCurrentUser && <ButtonWrapper shiftRight>
          <ConnectButton config={CONFERENCE_ERROR_CONFIG[kind]} withExternalIcon />
        </ButtonWrapper>}
    </Container>;
};

require("./conference_error.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./conference_error.js");